import { useParams, Link } from "react-router-dom";
import StickyHeader from "@/components/StickyHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTAFooter from "@/components/CTAFooter";
import { serviceContent } from "@/data/serviceContent";
import { ArrowLeft, Phone, ChevronDown, ChevronUp, Play } from "lucide-react";
import { useState } from "react";

const ServiceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const content = slug ? serviceContent[slug] : null;
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  if (!content) {
    return (
      <div className="min-h-screen bg-background">
        <StickyHeader />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">Service Not Found</h1>
          <Link to="/services" className="text-primary hover:underline">Return to Services</Link>
        </div>
        <CTAFooter />
      </div>
    );
  }

  const paragraphs = (text: string) =>
    text.split(/\\n\\n|\\n/).filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      <StickyHeader />
      <Breadcrumbs items={[{ label: "Our Services", href: "/services" }, { label: content.title }]} />

      {/* Top Navigation Bar */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <Link
            to="/services"
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors border border-border rounded-full px-4 py-2"
          >
            <ArrowLeft className="w-4 h-4" />
            RETURN TO SERVICES
          </Link>
          <a
            href="tel:0480841485"
            className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors border border-primary rounded-full px-4 py-2"
          >
            CONTACT US NOW
            <Phone className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold font-montserrat text-foreground mb-6 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
                {content.title}
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {content.heroDescription}
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src={content.image}
                alt={`${content.title} — professional air conditioning service by ACG Southern Highlands`}
                className="w-full h-64 md:h-80 object-cover"
                width={600}
                height={320}
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-2 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            Services
          </h2>
          <p className="text-xl italic text-muted-foreground mb-6">We Offer</p>

          {/* Intro Text */}
          <div className="prose prose-lg max-w-none text-muted-foreground mb-10">
            {paragraphs(content.introText).map((p, i) => (
              <p key={i} className="mb-4 leading-relaxed">{p}</p>
            ))}
          </div>

          {/* Service Cards Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {content.servicesWeOffer.map((service, idx) => (
              <div
                key={idx}
                className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg md:text-xl font-bold italic text-foreground mb-3 font-montserrat">
                  {service.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance / Key Info Section */}
      <section className="py-12 md:py-16 bg-acg-charcoal text-primary-foreground">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat mb-6 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            {content.maintenanceTitle}
          </h2>
          <div className="max-w-4xl">
            {paragraphs(content.maintenanceText).map((p, i) => (
              <p key={i} className="mb-4 leading-relaxed text-primary-foreground/80">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose ACG */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-6 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            Why Choose ACG?
          </h2>
          <div className="max-w-4xl">
            {paragraphs(content.whyChooseACG).map((p, i) => (
              <p key={i} className="mb-4 text-muted-foreground leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Step by Step */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-2 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            What's Involved
          </h2>
          <p className="text-xl italic text-muted-foreground mb-8">Step by Step</p>
          <div className="max-w-4xl space-y-5">
            {content.stepByStep.map((s, i) => (
              <div key={i} className="flex gap-4 bg-card border border-border rounded-lg p-5">
                <div className="flex-1">
                  <h3 className="font-bold text-foreground mb-1">{s.step}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{s.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cool Climate Considerations */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-6 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            Cool-Climate Considerations
          </h2>
          <div className="max-w-4xl">
            <p className="text-muted-foreground leading-relaxed">{content.coolClimateConsiderations}</p>
          </div>
        </div>
      </section>

      {/* Costs and Efficiency */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-6 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            Costs & Efficiency
          </h2>
          <div className="max-w-4xl">
            <p className="text-muted-foreground leading-relaxed">{content.costsAndEfficiency}</p>
          </div>
        </div>
      </section>

      {/* Spec Table */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-6 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            At a Glance
          </h2>
          <div className="max-w-2xl bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {content.specTable.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-muted/40" : ""}>
                    <td className="px-4 py-3 font-semibold text-foreground w-1/3 align-top">{row.label}</td>
                    <td className="px-4 py-3 text-muted-foreground align-top">{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-8 text-center pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {[...content.faqItems, ...content.additionalFaqItems].map((faq, idx) => (
              <div
                key={idx}
                className="bg-card border border-border rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex justify-between items-center p-5 text-left hover:bg-muted/50 transition-colors"
                >
                  <span className="font-semibold text-foreground pr-4">{faq.question}</span>
                  {openFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  )}
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5 text-muted-foreground leading-relaxed border-t border-border pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube Videos Section */}
      {content.youtubeVideos && content.youtubeVideos.length > 0 && (
        <section className="py-12 md:py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold font-montserrat text-foreground mb-2 pl-5 border-l-[3px] border-t-[3px] border-secondary pt-3 inline-block">
              Watch
            </h2>
            <p className="text-xl italic text-muted-foreground mb-8">Our Expert Videos</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.youtubeVideos.map((video, idx) => (
                <div key={idx} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-video">
                    <iframe
                      src={`https://www.youtube-nocookie.com/embed/${video.id}`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
                      <Play className="w-4 h-4 text-primary flex-shrink-0" />
                      {video.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: content.title,
            description: content.heroDescription,
            provider: {
              "@type": "LocalBusiness",
              name: "ACG Southern Highlands",
              address: {
                "@type": "PostalAddress",
                addressLocality: "the Southern Highlands",
                addressRegion: "Southern Highlands",
                addressCountry: "AU",
              },
            },
            areaServed: "the Southern Highlands, Southern Highlands, Australia",
          }),
        }}
      />

      <CTAFooter />
    </div>
  );
};

export default ServiceDetail;
